import { environment } from './../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, from, Observable, switchMap, tap, throwError } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { SessionService } from '../core/session.service';
import { ApiService } from '../api.service';
import { Apis } from '../API';
import { Store } from '@ngrx/store';
import { AlertService } from '../alerts.service';
import { EncryptionService } from '../core/encryption.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private currentUserSubject: BehaviorSubject<any>;
    public currentUser: Observable<any>;

    // Role-based permissions mapping
    private ROLE_PERMISSIONS = {
        ADMI: [ 'PECP', 'INFO', 'RECP', 'ADHA', 'EPA', 'ALDA', 'FDSA', 'PECA', 'RECA', 'DSHC', 'ADHP', 'CONF', 'CONP', 'PART', 'UTIL','STAT'],
        UMAS: [ 'PECP', 'INFO', 'RECP', 'ADHA', 'EPA',  'ALDA', 'FDSA', 'PECA', 'RECA', 'DSHC'],
        AGEN: [ 'DSHC']
    };

    constructor(
        private http: HttpClient,
        private router: Router,
        private sessionService: SessionService,
        private apiService: ApiService,
        private store: Store<any>,
        private alertService: AlertService,
        private encryptionService: EncryptionService,
    ) {
        this.currentUserSubject = new BehaviorSubject<any>(this.getUserData());
        this.currentUser = this.currentUserSubject.asObservable();
    }

    // ===============================
    // AUTHENTICATION METHODS
    // ===============================

    /**
     * Check if user is authenticated with valid token and user data
     */
    isAuthenticated(): boolean {
        const token = this.getToken();
        const userData = this.getUserData();
        
        return !!(token && userData && userData.code_role);
    }

    /**
     * Alias for isAuthenticated for backward compatibility
     */
    isLoggedIn(): boolean {
        return this.isAuthenticated();
    }

    /**
     * Login user with credentials
     */
    login(credentials: any): Observable<any> {
        return from(this.encryptionService.encryptBody(credentials)).pipe(
            switchMap(encryptedBody =>
                this.http.post(
                    `${environment.api_url}${Apis.authentication.connexion}`,
                    encryptedBody,
                    { headers: { 'X-Encrypted': 'true' } }
                )
            ),
            tap((response: any) => {
                if (response?.token) {
                    this.storeUserData(response);
                }
            }),
        );
    }

    /**
     * Logout user with proper cleanup
     */
    logout() {
        const token = this.getToken();
        
        this.store.dispatch({ type: 'toggleMainLoader', payload: true });
        
        if (token) {
            this.apiService
                .post(Apis.authentication.deconnexion, {})
                .pipe(
                    tap(() => {
                        this.clearSession();
                        this.store.dispatch({ type: 'toggleMainLoader', payload: false });
                    }),
                    catchError((error) => {
                        console.error('Logout failed, clearing session anyway:', error);
                        this.alertService.showMessage(
                            'Une erreur est survenue lors de la déconnexion',
                            'error',
                            'top'
                        );
                        this.clearSession();
                        this.store.dispatch({ type: 'toggleMainLoader', payload: false });
                        return throwError(() => error);
                    }),
                )
                .subscribe();
        } else {
            this.clearSession();
            this.store.dispatch({ type: 'toggleMainLoader', payload: false });
        }
    }

    // ===============================
    // USER DATA METHODS
    // ===============================

    /**
     * Store user authentication data
     */
    public storeUserData(response: any): void {
        const { token, user } = response;
        this.sessionService.setItem('token', token, true);
        this.sessionService.setItem('user_data', JSON.stringify(user), true);
        this.currentUserSubject.next(user);
    }

    /**
     * Get current user data from session
     */
    public getUserData(): any {
        try {
            const userData = this.sessionService.getItem('user_data');
            if (!userData) return null;
            
            return JSON.parse(userData);
        } catch (error) {
            console.error('Error parsing user data:', error);
            return null;
        }
    }

    /**
     * Get authentication token
     */
    getToken(): string | null {
        return this.sessionService.getItem('token');
    }

    // ===============================
    // PERMISSION METHODS
    // ===============================

    /**
     * Check if user has access to a specific module
     */
    hasModuleAccess(moduleCode: string): boolean {
        const user = this.getUserData();
        
        if (!user) {
            return false;
        }

        if (user.has_custom_permissions === 0) {
            // Check by role-based permissions
            const rolePermissions = this.ROLE_PERMISSIONS[user.code_role as keyof typeof this.ROLE_PERMISSIONS];
            return rolePermissions ? rolePermissions.includes(moduleCode) : false;
        } else if (user.has_custom_permissions === 1) {
            // Check by custom permissions
            if (!user.permissions) {
                return false;
            }
            const userPermissions = user.permissions.split(',').map((p: string) => p.trim());
            return userPermissions.includes(moduleCode);
        }

        return false;
    }

    /**
     * Get all accessible module codes for current user
     */
    getUserPermissions(): string[] {
        const user = this.getUserData();
        if (!user || !user.code_role) return [];

        if (user.has_custom_permissions === 0) {
            // Return role-based permissions
            return this.ROLE_PERMISSIONS[user.code_role as keyof typeof this.ROLE_PERMISSIONS] || [];
        } else if (user.has_custom_permissions === 1) {
            // Return custom permissions
            if (!user.permissions) return [];
            return user.permissions.split(',').map((p: string) => p.trim());
        }

        return [];
    }

    /**
     * Check if user has any permissions at all
     */
    hasAnyPermissions(): boolean {
        const permissions = this.getUserPermissions();
        return permissions.length > 0;
    }

    // ===============================
    // PRIVATE METHODS
    // ===============================

    /**
     * Clear session data and redirect to login
     */
    private clearSession() {
        this.sessionService.removeItem('token');
        this.sessionService.removeItem('user_data');
        this.currentUserSubject.next(null);
        this.router.navigate(['/auth/login']);
    }
}